import { Link } from "react-router";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import {
  useApproveGreenhouseTransfer,
  usePendingGreenhouseTransfers,
  useRejectGreenhouseTransfer,
} from "../hooks/useGreenhouseTransfers";
import type { GreenhouseTransferRequestDto } from "../services/greenhouseTransfersApi";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ApproveModal = ({
  transfer,
  open,
  onOpenChange,
}: {
  transfer: GreenhouseTransferRequestDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [comment, setComment] = useState("");
  const approve = useApproveGreenhouseTransfer();

  const handleApprove = () => {
    if (!transfer) return;
    approve.mutate(
      { id: transfer.id, payload: { decisionComment: comment || undefined, rowVersion: transfer.rowVersion } },
      { onSuccess: () => { onOpenChange(false); setComment(""); } }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve Transfer</DialogTitle>
          <DialogDescription>
            Approve transfer of {transfer?.trayCount} trays from{" "}
            {transfer?.sourceGreenhouseName} to {transfer?.destinationGreenhouseName}?
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel>Comment (optional)</FieldLabel>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional approval comment..."
          />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApprove} disabled={approve.isPending}>
            {approve.isPending ? "Approving..." : "Approve Transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const GreenhouseTransferApprovalsPage = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const { data: pendingPage, isLoading } = usePendingGreenhouseTransfers(pageNumber);
  const pending = pendingPage?.items ?? [];
  useEffect(() => {
    if (pendingPage && pageNumber > Math.max(1, pendingPage.totalPages))
      setPageNumber(Math.max(1, pendingPage.totalPages));
  }, [pendingPage, pageNumber]);
  const [approveTarget, setApproveTarget] = useState<GreenhouseTransferRequestDto | null>(null);
  const [rejectTarget, setRejectTarget] = useState<GreenhouseTransferRequestDto | null>(null);
  const reject = useRejectGreenhouseTransfer();

  const handleReject = (reason: string) => {
    if (!rejectTarget) return;
    reject.mutate(
      { id: rejectTarget.id, payload: { decisionComment: reason, rowVersion: rejectTarget.rowVersion } },
      { onSuccess: () => setRejectTarget(null) }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Greenhouse Transfer Approvals</h1>
          <Button variant="link" asChild><Link to="/production/greenhouse-transfers">Transfer history and notes</Link></Button>
          <p className="text-muted-foreground">
            Review and approve or reject pending tray transfer requests between greenhouses.
          </p>
        </div>
        {(pendingPage?.totalCount ?? 0) > 0 && (
          <Badge variant="secondary">{pendingPage?.totalCount} pending</Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending Transfer Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : pending.length === 0 ? (
            <p className="text-muted-foreground">No pending transfer requests.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Crop / Variety</TableHead>
                  <TableHead>Tray Type</TableHead>
                  <TableHead>Trays</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Requested At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>
                      {transfer.cropTypeName}
                      {transfer.variety && (
                        <span className="text-muted-foreground"> — {transfer.variety}</span>
                      )}
                    </TableCell>
                    <TableCell>{transfer.trayType ?? "—"}</TableCell>
                    <TableCell>{transfer.trayCount}</TableCell>
                    <TableCell>{transfer.sourceGreenhouseName}</TableCell>
                    <TableCell>{transfer.destinationGreenhouseName}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={transfer.reason}>
                      {transfer.reason}
                    </TableCell>
                    <TableCell>{transfer.requestedByUserName}</TableCell>
                    <TableCell>{formatDate(transfer.requestedAt)}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        onClick={() => setApproveTarget(transfer)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setRejectTarget(transfer)}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {(pendingPage?.totalPages ?? 0) > 1 && (
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={pageNumber <= 1}
                onClick={() => setPageNumber((page) => page - 1)}>Previous</Button>
              <span className="text-sm">Page {pageNumber} of {pendingPage?.totalPages}</span>
              <Button variant="outline" size="sm" disabled={pageNumber >= (pendingPage?.totalPages ?? 0)}
                onClick={() => setPageNumber((page) => page + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ApproveModal
        transfer={approveTarget}
        open={!!approveTarget}
        onOpenChange={(open) => { if (!open) setApproveTarget(null); }}
      />

      <ReasonDialog
        open={!!rejectTarget}
        onOpenChange={(open) => { if (!open) setRejectTarget(null); }}
        title="Reject Transfer"
        description={`Reject transfer of ${rejectTarget?.trayCount} trays from ${rejectTarget?.sourceGreenhouseName} to ${rejectTarget?.destinationGreenhouseName}?`}
        onSubmit={handleReject}
        isSubmitting={reject.isPending}
        submitLabel="Reject"
        reasonLabel="Rejection reason"
      />
    </div>
  );
};
